import { useCallback, useState } from 'react';
import { IconForward } from '@belong/icons';
import { Button, Text } from '@belong/ui';
import { MaintenanceRecurrenceSubTaskModel } from 'api/models';
import clsx from 'clsx';
import Collapsible from 'components/Collapsible/Collapsible';
import ItemDetail from 'components/ItemDetail/ItemDetail';
import { startCase } from 'es-toolkit/compat';
import {
  getDateByStatus,
  getStatus,
} from 'pages/Accounts/Components/maintenance-recurrence/maintenance-recurrence.utils';
import { DATE_FORMATS, formatDate } from 'utils/formatDate';

const COLLAPSED_MAX_ITEMS_VISIBLE = 3;

type MaintenanceRecurrentSubtasksListProps = {
  subtasks: MaintenanceRecurrenceSubTaskModel[];
  onSubtaskClick: (subtask: MaintenanceRecurrenceSubTaskModel) => void;
};

export const MaintenanceRecurrenceSubtasksList = ({
  subtasks,
  onSubtaskClick,
}: MaintenanceRecurrentSubtasksListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderItem = useCallback(
    (subtask, index) => {
      if (!isExpanded && index > COLLAPSED_MAX_ITEMS_VISIBLE - 1) {
        return null;
      }

      const upcomingDate = getDateByStatus(subtask);
      const upcomingStatus = getStatus(subtask.subTaskStatus);

      return (
        <Button onClick={() => onSubtaskClick(subtask.uniqueId)} variant="text">
          <ItemDetail
            className="group mb-0 items-start md:items-center"
            key={subtask.uniqueId}
            firstItem={
              <div className="flex flex-col items-start justify-start gap-2xs md:flex-row md:items-center">
                <Text className="capitalize" variant="body">
                  <span className="font-semibold md:font-regular">
                    {startCase(subtask.summary)}
                  </span>
                </Text>
                {upcomingDate && (
                  <Text className="capitalize" variant="body">
                    <span className="hidden md:inline"> | </span>
                    {formatDate(upcomingDate, DATE_FORMATS.DOTTED_DAY_MEDIUM)}
                  </Text>
                )}
              </div>
            }
            secondItem={
              <div className="flex items-center">
                <Text
                  as="span"
                  className={clsx(
                    'pr-xs text-right capitalize',
                    upcomingStatus.textClassName,
                  )}
                  fontWeight="semibold"
                  variant="body"
                >
                  {upcomingStatus.label}
                </Text>
                <IconForward
                  height={13}
                  onClick={() => onSubtaskClick(subtask.uniqueId)}
                />
              </div>
            }
          />
        </Button>
      );
    },
    [isExpanded, onSubtaskClick],
  );

  return (
    <Collapsible
      defaultClassName="block"
      title={`Services (${subtasks.length})`}
      collapsed={false}
      inner={
        <div className="mt-xs flex flex-col items-stretch gap-sm">
          {subtasks.map(renderItem)}
          {subtasks.length > COLLAPSED_MAX_ITEMS_VISIBLE && (
            <div className="self-end pt-sm">
              <Button
                onClick={() => setIsExpanded((state) => !state)}
                variant="text"
              >
                {isExpanded ? 'SHOW LESS' : 'SHOW MORE'}
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
};
